import { useEffect, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import {
  useBranding,
  useSaveUiSettings,
  useUiSettings,
  useUserProfile,
} from '@/features/auth/api';
import { serializeTheme, useThemeStore } from '@/lib/theme/store';
import type { SerializedTheme } from '@/lib/theme/store';
import { Header } from './Header';
import { Sidebar, TopBar } from './Sidebar';
import { SidebarDocker, SidebarDragHandle } from './SidebarDocker';
import { getUser } from '@/utilities/setting';
import { BACKEND_API_BASE_URL } from '@/utilities/constant';
import { useAuthedImageUrl } from '@/hooks/useAuthedImageUrl';
import { useSessionRefresh } from '@/hooks/useSessionRefresh';
import useAdminBreakNotifications from '@/hooks/useAdminBreakNotifications';
import useSystemHealthAlerts from '@/hooks/useSystemHealthAlerts';
import useSessionRecorderGate from '@/hooks/useSessionRecorderGate';
import { storageUrlOr } from '@/utilities/storageUrl';

/** Debounce window between a theme-store change and the server save. */
const SAVE_DEBOUNCE_MS = 1500;

/**
 * Parse a theme blob safely. Accepts a JSON string or an already-parsed object.
 * Returns `null` on any error so callers can keep existing state.
 */
function safeParseTheme(json: unknown): Partial<SerializedTheme> | null {
  if (json == null || json === '') return null;
  try {
    if (typeof json === 'object' && !Array.isArray(json)) {
      return json as Partial<SerializedTheme>;
    }
    if (typeof json !== 'string') return null;
    const parsed = JSON.parse(json);
    if (parsed && typeof parsed === 'object') return parsed as Partial<SerializedTheme>;
    return null;
  } catch {
    return null;
  }
}

/**
 * Authenticated shell. The sidebar placement (`left` / `right` / `top`) is
 * read from the theme store; the layout reorders accordingly. The whole
 * shell is wrapped in <SidebarDocker> so the drag handle in the sidebar
 * can dock to any edge.
 */
export function AppShell() {
  useSessionRefresh();
  // Connects adminSocket for reseller/company/call-centre-admin/team-lead
  // roles (self-gates internally by role) — mirrors legacy
  // cloudtelephony_frontend's layouts/dashboard/default.js, which calls
  // this unconditionally from the one layout every authenticated role
  // mounts. This hook existed here before but was never actually called
  // anywhere, so adminSocket never connected for those roles.
  useAdminBreakNotifications();
  useSystemHealthAlerts();
  useSessionRecorderGate();

  const placement = useThemeStore((s) => s.sidebarPlacement);
  const hydrateFromServer = useThemeStore((s) => s.hydrateFromServer);
  const setHydrating = useThemeStore((s) => s.setHydrating);
  const branding = useBranding();
  const uiSettings = useUiSettings();
  const saveUiSettings = useSaveUiSettings();
  const profile = useUserProfile();

  /*
   * Panel branding comes off the logged-in user's COMPANY: the backend walks
   * the user's assigned_company and, when that company sets no branding of its
   * own, up through its parent companies, and hands the result back as the
   * effective_* fields. The per-host `/branding/<hostname>` endpoint is only a
   * fallback for the pre-login / unbranded case.
   */
  const brandUser = getUser();
  const panelDisplayName =
    brandUser?.effective_panel_display_name || branding.data?.company || null;
  const logoPath = brandUser?.effective_logo_path || null;
  const logoBlobPath = brandUser?.effective_logo_blob_path || null;
  const rawLogoUrl = logoPath
    ? storageUrlOr('logo', logoBlobPath, `${BACKEND_API_BASE_URL.replace('/api', '')}/${logoPath}`)
    : (branding.data?.logoUrl ?? null);
  const { src: resolvedLogoUrl } = useAuthedImageUrl(rawLogoUrl);

  // Reflect the branded panel name in the document title.
  useEffect(() => {
    if (panelDisplayName) {
      document.title = panelDisplayName;
    }
  }, [panelDisplayName]);

  /* ---- Hydrate the theme store on login ---------------------------------
   *
   * Two layers, applied in order so later overlays win:
   *   1. Per-domain `uiDefaults` from the branding endpoint.
   *   2. Per-user `uiSettings` from /api/v2/user-appearance-preferences/me.
   *
   * We toggle `isHydrating` while either query is loading so the debounced
   * save subscriber below doesn't echo the hydration straight back to the
   * server. */
  // Only block saves while an *enabled* settings fetch is in flight.
  // Branding (/api/obd/...) often 404s on this stack — don't leave isHydrating stuck.
  useEffect(() => {
    const uiBusy = uiSettings.isFetching || uiSettings.isLoading;
    const brandingBusy = branding.isFetching || branding.isLoading;
    setHydrating(uiBusy || brandingBusy);
  }, [
    uiSettings.isFetching,
    uiSettings.isLoading,
    branding.isFetching,
    branding.isLoading,
    setHydrating,
  ]);

  // Safety: never leave hydrating stuck (e.g. hanging branding request).
  useEffect(() => {
    const t = setTimeout(() => setHydrating(false), 8000);
    return () => clearTimeout(t);
  }, [setHydrating]);

  useEffect(() => {
    const defaults = safeParseTheme(branding.data?.uiDefaults);
    if (defaults) hydrateFromServer(defaults);
  }, [branding.data?.uiDefaults, hydrateFromServer]);

  useEffect(() => {
    const overlay = safeParseTheme(uiSettings.data?.json);
    if (overlay) hydrateFromServer(overlay);
  }, [uiSettings.data?.json, hydrateFromServer]);

  /* ---- Debounced save on every theme-store change ---------------------- */
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSentRef = useRef<string | null>(null);

  useEffect(() => {
    // Subscribe outside React's render cycle so we don't trigger a re-render
    // on every keystroke-equivalent setter. The closure captures the latest
    // mutation handle via the ref pattern.
    const unsub = useThemeStore.subscribe((state, prev) => {
      if (state.isHydrating) return;
      // Skip if the persisted slice didn't actually change (e.g. only
      // isHydrating flipped).
      const next = JSON.stringify(serializeTheme(state));
      const prevSerialized = JSON.stringify(serializeTheme(prev));
      if (next === prevSerialized) return;
      if (next === lastSentRef.current) return;

      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        lastSentRef.current = next;
        saveUiSettings.mutate(next, {
          onError: (err) => {
            console.warn('[appearance] save failed:', err);
            // Allow retry on next change
            lastSentRef.current = null;
          },
        });
      }, SAVE_DEBOUNCE_MS);
    });
    return () => {
      unsub();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [saveUiSettings]);

  const brand = {
    logoUrl: resolvedLogoUrl,
    company: panelDisplayName,
  };

  const isHorizontal = placement === 'top';
  const isRight = placement === 'right';

  /*
   * Layout strategy — fixed viewport height with scrollable <main>.
   *
   * The root container is h-screen + overflow-hidden so the sidebar and
   * header stay pinned. <main> owns page scroll (overflow-y-auto), so
   * every ported page — the majority of which don't add their own
   * scroll wrapper — scrolls out of the box.
   *
   * Pages that host their own internal scroll region (e.g. DataTable-
   * heavy pages using flex-1 min-h-0 + overflow inside a card) still
   * work: their content sits inside <main> and only reaches main's
   * scroll when its own container overflows.
   *
   * Previous attempt made <main> non-scrolling and required every page
   * to wrap itself. The v1-verbatim ports never did that, which is why
   * scroll appeared broken across the app.
   */
  return (
    <SidebarDocker>
      <div className="flex h-screen overflow-hidden bg-background">
        {!isHorizontal && !isRight && (
          <Sidebar brand={brand} showDragHandle dragHandle={<SidebarDragHandle />} />
        )}
        <div className="flex min-w-0 flex-1 flex-col">
          {(() => {
            // AppShell has two possible user identity sources:
            //   1. `profile.data` — GET /api/obd/user/profile/:id (Java backend
            //      flow). Populated only when the login went through the
            //      Java `POST /api/obd/login` path that puts userId in
            //      useAuthStore.
            //   2. `Storage.getItem('user')` — v1 Cloud Telephony login
            //      flow. This is what every ported CTI page/widget reads
            //      and it always has username/name for agents.
            // Prefer Storage so agent logins (v1 flow) always show their
            // username in the avatar; fall back to profile.data for
            // sessions started via the Java flow.
            const storageUser: any = getUser();
            const assignedCompany =
              storageUser?.assignedCompany ||
              storageUser?.assigned_company_details ||
              storageUser?.company;
            return (
              <Header
                username={
                  profile.data?.username
                    ?? profile.data?.name
                    ?? storageUser?.username
                    ?? storageUser?.name
                    ?? ''
                }
                voiceCredits={
                  profile.data?.credits ??
                  assignedCompany?.credits_available ??
                  storageUser?.credits_available ??
                  storageUser?.voice_credits
                }
                effectiveVoiceCredits={
                  assignedCompany?.credits_distributable ??
                  assignedCompany?.credits_available
                }
                ttsCredits={
                  profile.data?.ttsCredits ??
                  assignedCompany?.tts_credits ??
                  storageUser?.tts_credits
                }
                effectiveTtsCredits={
                  assignedCompany?.tts_credits_distributable ??
                  assignedCompany?.tts_credits
                }
                aiAgentCredits={
                  assignedCompany?.ai_agent_credits ??
                  storageUser?.ai_agent_credits
                }
                effectiveAiAgentCredits={
                  assignedCompany?.ai_agent_credits_distributable ??
                  assignedCompany?.ai_agent_credits
                }
              />
            );
          })()}
          {isHorizontal && <TopBar brand={brand} dragHandle={<SidebarDragHandle />} />}
          <main className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            <Outlet />
          </main>
        </div>
        {!isHorizontal && isRight && (
          <Sidebar brand={brand} showDragHandle dragHandle={<SidebarDragHandle />} />
        )}
      </div>
    </SidebarDocker>
  );
}
